import {
  createHistoryUploader,
  sleep,
  uploadParts,
  uploadSource,
  xhrPutPart,
  type HistoryUploader,
  type UploadPartsOptions,
} from '../lib/historyUpload'

// A history import's upload (ImportActivitiesLive, and UserConnectedAppsLive's
// Garmin card for a rider without the upload page): uploads what's picked in
// any file input marked `data-history-upload`, or dropped on an element marked
// `data-history-drop`.
// One zip goes up as it is; anything else is wrapped into one zip first. The
// logic is in lib/historyUpload.ts.
//
// While an element marked `data-history-uploading` is on the page, leaving it
// warns.

// The promise form: it rejects when the push fails (no connection, a
// timeout), where the callback form never calls back.
function pushEventWithReply(self: any) {
  return function pushEvent(event: string, payload: Record<string, unknown>): Promise<unknown> {
    return self.pushEvent(event, payload)
  }
}

function uploadPartsWithXhr(options: UploadPartsOptions) {
  return uploadParts(options, { putPart: xhrPutPart, sleep })
}

function dropZone(event: Event): Element | null {
  const target = event.target
  if (!target || typeof (target as Element).closest !== 'function') {
    return null
  }

  return (target as Element).closest('[data-history-drop]')
}

export const HistoryUpload = {
  mounted() {
    const self = this as any

    const uploader: HistoryUploader = createHistoryUploader({
      pushEvent: pushEventWithReply(self),
      sleep,
      uploadParts: uploadPartsWithXhr,
    })
    self.uploader = uploader

    function startWith(files: File[]) {
      if (files.length === 0) {
        return
      }

      uploader.start(uploadSource(files))
    }

    self.onChange = function onChange(event: Event) {
      const input = event.target
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-history-upload]')) {
        return
      }

      const files = Array.from(input.files || [])
      input.value = ''
      startWith(files)
    }

    self.onDragOver = function onDragOver(event: DragEvent) {
      const zone = dropZone(event)
      if (!zone) {
        return
      }

      event.preventDefault()
      zone.setAttribute('data-dragover', '')
    }

    self.onDragLeave = function onDragLeave(event: DragEvent) {
      const zone = dropZone(event)
      if (zone) {
        zone.removeAttribute('data-dragover')
      }
    }

    self.onDrop = function onDrop(event: DragEvent) {
      const zone = dropZone(event)
      if (!zone) {
        return
      }

      event.preventDefault()
      zone.removeAttribute('data-dragover')
      startWith(Array.from(event.dataTransfer?.files || []))
    }

    document.addEventListener('change', self.onChange)
    document.addEventListener('dragover', self.onDragOver)
    document.addEventListener('dragleave', self.onDragLeave)
    document.addEventListener('drop', self.onDrop)

    self.handleEvent('history_upload_abort', function onAbort(payload: { import_id: number }) {
      uploader.abort(payload.import_id)
    })

    self.onBeforeUnload = function onBeforeUnload(event: BeforeUnloadEvent) {
      if (document.querySelector('[data-history-uploading]')) {
        event.preventDefault()
      }
    }
    window.addEventListener('beforeunload', self.onBeforeUnload)
  },

  // The LiveView remounted: it shows the running upload again once told.
  reconnected() {
    const self = this as any
    self.uploader.reportState()
  },

  destroyed() {
    const self = this as any
    document.removeEventListener('change', self.onChange)
    document.removeEventListener('dragover', self.onDragOver)
    document.removeEventListener('dragleave', self.onDragLeave)
    document.removeEventListener('drop', self.onDrop)
    window.removeEventListener('beforeunload', self.onBeforeUnload)
    self.uploader.stop()
  },
}
